import { sellersGuideContent } from '@/assets/sellers-guide';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MotionButton } from '@/components/ui/motion-button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { slideInFromBottom, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { cn } from '@/lib/utils';
import { createFileRoute, Link, useLocation } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight, Home, Link2, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

export const Route = createFileRoute('/_public/sellers-guide')({
  component: SellersGuidePage,
})
export default function SellersGuidePage() {
  useEffect(() => {
    document.title = "Seller's Guide | Swim Homes";
  }, []);

  // Use localStorage to remember the sidebar state across visits
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    // Default to open on desktop, closed on mobile
    const savedState = localStorage.getItem('sellersSidebarOpen');
    if (savedState !== null) {
      return savedState === 'true';
    }
    return window.innerWidth >= 768;
  });

  const [activeSection, setActiveSection] = useState('introduction');
  const location = useLocation();
  const mainContentRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const sectionRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sellersSidebarOpen', isSidebarOpen.toString());
  }, [isSidebarOpen]);

  // Handle initial hash navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
          setActiveSection(hash);
        }, 100);
      }
    }
  }, [location]);

  // Handle scroll to update active section without manipulating history
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-section]');

      if (sections.length === 0) return;

      // Find the section that's most visible in the viewport
      let closestElement: Element | null = null;
      let closestDistance = Infinity;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top);

        if (distance < closestDistance && rect.top <= 100) {
          closestDistance = distance;
          closestElement = section;
        }
      });

      if (closestElement) {
        // @ts-expect-ignore - TypeScript doesn't correctly identify the id property on the element
        const sectionId = closestElement.id;
        if (sectionId && sectionId !== activeSection) {
          setActiveSection(sectionId);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection]);

  // Scroll the active section link into view in the sidebar
  useEffect(() => {
    const activeLink = sectionRefs.current[activeSection];
    if (activeLink && sidebarRef.current) {
      activeLink.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeSection]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
    // Close sidebar on mobile after clicking a section
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <>
      <div className="min-h-screen bg-background text-foreground relative z-10">
        {/* Hero Section */}
        <section className="relative bg-linear-to-br from-[#0074b7] to-[#0057a7] pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              viewport={viewportOptions}
            >
              <motion.span
                variants={staggerItem}
                className="inline-block px-4 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-6"
              >
                Guide
              </motion.span>
              <motion.h1
                variants={staggerItem}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
              >
                Complete Guide to <br />
                <span className="text-blue-200">Selling Your Home</span>
              </motion.h1>
              <motion.p
                variants={staggerItem}
                className="text-lg text-blue-100 opacity-90 max-w-2xl mb-6"
              >
                Your step-by-step resource to selling your home efficiently, maximizing your value,
                and keeping more money in your pocket.
              </motion.p>
            </motion.div>
          </div>

          {/* Wave divider */}
          <div className="absolute -bottom-1 left-0 w-full overflow-hidden leading-[0] z-10">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[60px] text-background" style={{ display: 'block' }}>
              <path d="M0,0V30c180,60,420,-40,600,10c100,30,220,70,350,30c80,-25,170,-50,250,-10V120H0Z" fill="currentColor"></path>
            </svg>
          </div>
        </section>

        {/* Content Wrapper with Sidebar and Main Content */}
        <div className="flex relative">
          {/* Mobile & Desktop menu button when sidebar is collapsed */}
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "fixed z-40 transition-opacity duration-200 top-20 left-4 flex items-center",
              isSidebarOpen ? "opacity-0 pointer-events-none" : "opacity-100 pointer-events-auto"
            )}
            onClick={toggleSidebar}
          >
            <Menu className="h-4 w-4 mr-1" />
            <span className="text-xs">Menu</span>
          </Button>

          {/* Sidebar */}
          <aside
            className={cn(
              "fixed inset-y-0 left-0 z-30 w-72 bg-background border-r transition-all duration-200",
              "pt-16 shadow-lg overflow-hidden flex flex-col",
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}
          >
            {/* Sidebar Header with Close Button */}
            <div className="flex items-center justify-between px-4 py-2 border-b bg-background">
              <h3 className="font-semibold text-primary">Guide Sections</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <ScrollArea className="flex-1 px-4" ref={sidebarRef}>
              <nav className="py-4">
                {sellersGuideContent.map((section) => (
                  <div key={section.id} className="mb-4">
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full justify-start font-medium",
                        activeSection === section.id && "bg-primary/10 text-primary"
                      )}
                      onClick={() => scrollToSection(section.id)}
                      ref={(el) => sectionRefs.current[section.id] = el}
                    >
                      <section.icon className="size-4 mr-2 inline" />
                      <span className="text-left">{section.title}</span>
                    </Button>

                    {section.subsections?.map((subsection) => (
                      <Button
                        key={subsection.id}
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "w-full justify-start pl-8 text-sm mt-1",
                          activeSection === subsection.id && "bg-primary/5 text-primary"
                        )}
                        onClick={() => scrollToSection(subsection.id)}
                        ref={(el) => sectionRefs.current[subsection.id] = el}
                      >
                        <span className="text-left">{subsection.title}</span>
                      </Button>
                    ))}
                  </div>
                ))}
              </nav>
            </ScrollArea>
          </aside>

          {/* Main Content */}
          <main
            className="w-full transition-all duration-200 pt-6"
            ref={mainContentRef}
          >
            <div className="max-w-4xl mx-auto py-8 px-4">
              <div className="prose prose-blue max-w-none dark:prose-invert">
                {/* Content sections */}
                {sellersGuideContent.map((section, index) => (
                  <motion.section
                    key={section.id}
                    id={section.id}
                    data-section
                    className="mb-16 scroll-mt-20"
                    variants={slideInFromBottom}
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportOptions}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <section.icon className="h-8 w-8 text-primary flex-shrink-0" />
                      <h2 className="text-3xl font-bold text-foreground m-0">
                        <div className="flex items-center gap-2 group">
                          {section.title}
                          <Link2
                            className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={(e) => {
                              const url = `${window.location.origin}${window.location.pathname}#${section.id}`;
                              navigator.clipboard.writeText(url);
                              e.stopPropagation();
                            }}
                          />
                        </div>
                      </h2>
                    </div>
                    <div className="text-lg mb-8 text-foreground">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {section.description}
                      </ReactMarkdown>
                    </div>
                    {section.subsections?.map((subsection) => (
                      <div
                        key={subsection.id}
                        id={subsection.id}
                        data-section
                        className="mb-8 scroll-mt-20"
                      >
                        <Card className="p-6 border border-border hover:border-primary/20 transition-colors duration-200">
                          <CardHeader className="pb-2">
                            <div className="flex items-center gap-3">
                              <CardTitle className="text-foreground text-xl">{subsection.title}</CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {subsection.description && (
                              <div className="mb-4 text-muted-foreground">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[rehypeRaw]}
                                >
                                  {subsection.description}
                                </ReactMarkdown>
                              </div>
                            )}
                            {subsection.content && (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw]}
                              >
                                {subsection.content.join("\n\n")}
                              </ReactMarkdown>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </motion.section>
                ))}

                {/* Enhanced CTA Section */}
                <div className="mt-16 relative overflow-hidden rounded-lg">
                  <motion.div
                    className="bg-linear-to-br from-[#0074b7] to-[#0057a7] p-8 md:p-10"
                    variants={slideInFromBottom}
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportOptions}
                  >
                    <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to Get Started?</h2>
                    <p className="text-blue-100 mb-6 text-lg">
                      Now that you understand the process, take the first step towards selling your home and keeping more money in your pocket.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 mt-4">
                      <MotionButton
                        size="lg"
                        className="w-full sm:w-auto bg-white text-[#0074b7] hover:bg-blue-50 border-2 border-white"
                        asChild
                      >
                        <Link to='/dashboard/edit'>
                          <Home className="mr-2 h-4 w-4" />
                          Create Your Listing
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </MotionButton>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
